import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import * as v from 'valibot';
import { X, Loader2, Info } from 'lucide-react';
import { useUpdateTeamMemberMutation } from '../../store/teamMemberApi';
import { toast } from '../../utils/toast';
import ConfirmEmailChangeDialog from './ConfirmEmailChangeDialog';

const editTeamMemberSchema = v.object({
  firstName: v.pipe(
    v.string(),
    v.minLength(2, 'First name must be at least 2 characters'),
    v.maxLength(50, 'First name must not exceed 50 characters')
  ),
  lastName: v.pipe(
    v.string(),
    v.minLength(2, 'Last name must be at least 2 characters'),
    v.maxLength(50, 'Last name must not exceed 50 characters')
  ),
  email: v.pipe(v.string(), v.email('Please enter a valid email address')),
  phone: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  role: v.picklist(
    ['Account Executive', 'Customer Success', 'Account Manager', 'Manager', 'Marketing Manager'] as const,
    'Please select a valid role'
  ),
});

type EditTeamMemberFormData = v.InferOutput<typeof editTeamMemberSchema>;

interface EditTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
    role: string;
  };
}

const EditTeamMemberModal: React.FC<EditTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'teams' });
  const [updateTeamMember, { isLoading }] = useUpdateTeamMemberMutation();
  const [pendingSubmit, setPendingSubmit] = useState<EditTeamMemberFormData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<EditTeamMemberFormData>({
    resolver: valibotResolver(editTeamMemberSchema),
    defaultValues: {
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone || '',
      role: member.role as EditTeamMemberFormData['role'],
    },
  });

  const submit = async (data: EditTeamMemberFormData) => {
    const emailChanged = data.email !== member.email;
    try {
      await updateTeamMember({
        id: member.id,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone || undefined,
          role: data.role,
        },
      }).unwrap();
      toast.success(
        emailChanged
          ? t('edit.updateSuccessEmail', { email: data.email })
          : t('edit.updateSuccess')
      );
      setPendingSubmit(null);
      onSuccess();
    } catch (err: any) {
      toast.error(err?.data?.message || t('edit.updateError'));
      setPendingSubmit(null);
    }
  };

  const onSubmit = async (data: EditTeamMemberFormData) => {
    if (data.email !== member.email) {
      setPendingSubmit(data);
      return;
    }
    await submit(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isLoading ? undefined : onClose} />
      <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t('edit.heading')}</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('add.email')}</label>
            <input
              {...register('email')}
              type="email"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>
            )}
            <div className="mt-2 flex items-start space-x-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{t('editModal.emailChangeInfo')}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('add.firstName')}</label>
              <input
                {...register('firstName')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.firstName && (
                <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('add.lastName')}</label>
              <input
                {...register('lastName')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.lastName && (
                <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('edit.phone')}</label>
            <input
              {...register('phone')}
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
              placeholder={t('edit.phonePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('add.role')}</label>
            <select
              {...register('role')}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                errors.role ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="Account Executive">{t('roles.accountExecutive')}</option>
              <option value="Customer Success">{t('roles.customerSuccess')}</option>
              <option value="Account Manager">{t('roles.accountManager')}</option>
              <option value="Manager">{t('roles.manager')}</option>
              <option value="Marketing Manager">{t('roles.marketingManager')}</option>
            </select>
            {errors.role && (
              <p className="text-xs text-red-600 mt-1">{errors.role.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !isDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-[#4339A0] rounded-lg hover:bg-[#362d85] transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{t('common:saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>

      <ConfirmEmailChangeDialog
        isOpen={pendingSubmit !== null}
        onCancel={() => setPendingSubmit(null)}
        onConfirm={() => {
          if (pendingSubmit) submit(pendingSubmit);
        }}
        isLoading={isLoading}
        currentEmail={member.email}
        newEmail={pendingSubmit?.email || ''}
      />
    </div>
  );
};

export default EditTeamMemberModal;
